import { useEffect, useRef, useState } from 'react';
import { call, type GetToken, type GroupSummary } from '../api';

/**
 * Accepting an invitation (R8, R10).
 *
 * **One refusal message, for every refusal, naming no group.** Unknown token, wrong
 * recipient, unverified address, expired, revoked, already used, group full, inviter no
 * longer an admin — the Worker answers all eight with the same body on purpose, and this
 * screen must not undo that by rendering eight different sentences from the same answer.
 * The reason is recorded server-side, in the event and in `invitations.last_refusal`, where
 * the admin who typed the address can see it and re-invite. That is why the copy points at
 * that person rather than at a support address.
 *
 * The token is cleared once it has been used — including after a refusal. It is single-use
 * by the Worker's rules whatever happened (KTD9), and a token kept past its answer is a
 * token that survives into whatever this tab does next.
 */

/** The only thing a refused join ever says. */
export const JOIN_REFUSED =
  "This invitation link isn't working. Ask whoever invited you to send a new one.";

export interface JoinPageProps {
  /** Held in `sessionStorage` by `invite-token.ts`; null when there is nothing to accept. */
  token: string | null;
  getToken: GetToken;
  onJoined: (group: GroupSummary) => void;
  /** Called once the token has been spent, refused, or found missing. */
  onSpent: () => void;
}

type Stage = 'working' | 'joined' | 'refused' | 'unavailable';

export default function JoinPage({ token, getToken, onJoined, onSpent }: JoinPageProps) {
  const [stage, setStage] = useState<Stage>('working');
  // Single-use means single-post. StrictMode double-invokes effects in development, and a
  // second accept would race the first through the rate limiter for no benefit.
  const posted = useRef(false);

  useEffect(() => {
    if (posted.current) return;
    posted.current = true;

    let live = true;
    void (async () => {
      if (token === null) {
        // No token at all is a refusal like any other, and it says the same thing. A
        // separate "no invitation found" message would tell someone probing `/join`
        // something a real refusal does not.
        if (live) setStage('refused');
        onSpent();
        return;
      }

      const result = await call<{ group: GroupSummary }>(getToken, '/api/invitations/accept', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!live) return;

      onSpent();

      if (result.ok) {
        setStage('joined');
        onJoined(result.data.group);
        return;
      }

      // A network failure or a 503 is not a refusal — the link may be perfectly good and
      // nothing was decided about it. Saying "this link isn't working" there would send a
      // teacher back to their colleague for a replacement they do not need.
      setStage(result.status === 0 || result.status >= 500 ? 'unavailable' : 'refused');
    })();

    return () => { live = false; };
  }, [token, getToken, onJoined, onSpent]);

  return (
    <section className="panel" aria-label="Invitation">
      {stage === 'working' && <p className="hint" role="status">Checking your invitation…</p>}

      {stage === 'joined' && <p className="hint" role="status">You are in. Opening the group…</p>}

      {stage === 'refused' && <p className="error" role="alert">{JOIN_REFUSED}</p>}

      {stage === 'unavailable' && (
        <p className="error" role="alert">
          We could not reach Collab just now. Your link is unaffected — try opening it again in a
          moment.
        </p>
      )}
    </section>
  );
}
